import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3206';
import {
  doCopyDataAccess,
  luHandleAppiError,
  normalizeU3206AiUserGroupFromApi,
  type U3206AiUserGroupFormRow,
} from '../U3206Service';

export function useU3206CopyDataAccessHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const [copying, setCopying] = useState(false);

  const copyDataAccessFromGroup = useCallback(
    async (targetGroupId: string, sourceGroupId: string): Promise<U3206AiUserGroupFormRow | null> => {
      setCopying(true);
      try {
        const response = await doCopyDataAccess(axios, targetGroupId, { sourceGroupId });
        const row = response.data?.result ?? response.data;
        const normalized = normalizeU3206AiUserGroupFromApi(row as U3206AiUserGroupFormRow);
        toast.success(t('msgDataAccessCopied', { ns: NS }));
        return normalized;
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        setCopying(false);
      }
    },
    [axios, t, toast],
  );

  return { copying, copyDataAccessFromGroup };
}
